import * as React from 'react';

import { enumSalesOrderStatus } from '@rematter/frontend-lib-api-client';
import { cn } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  $effectiveNotesById,
  $optimisticNotesById,
  $salesOrderDetailById,
  $updateSalesOrder,
} from '../../_state/salesOrderDetail.state';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { EntityTabNotes } from '~/shared/components/EntityTabNotes';

type Props = {
  salesOrderId: string;
  variant?: 'default' | 'embedded';
};

export const SalesOrderTabNotes: React.FC<Props> = ({
  salesOrderId,
  variant = 'default',
}) => {
  const t = useTranslations();
  const toast = useToast();
  const updateSalesOrderMutation = useSetAtom($updateSalesOrder);

  const salesOrderData = useAtomValue($salesOrderDetailById(salesOrderId));
  const salesOrder = salesOrderData?.data;

  const effectiveNotes = useAtomValue($effectiveNotesById(salesOrderId));
  const setOptimisticNotes = useSetAtom($optimisticNotesById(salesOrderId));

  const isReadonly =
    salesOrder?.status === enumSalesOrderStatus.Finished ||
    salesOrder?.status === enumSalesOrderStatus.Voided;

  const handleSave = React.useCallback(
    async (notes: { internalNotes: string; externalNotes: string }) => {
      await updateSalesOrderMutation({
        id: salesOrderId,
        recyclerInternalNotes: notes.internalNotes,
        recyclerExternalNotes: notes.externalNotes,
      });
      toast.success(t.salesOrderDetail.notes.updated);
    },
    [salesOrderId, updateSalesOrderMutation, toast, t],
  );

  const handleChange = React.useCallback(
    (notes: { internalNotes: string; externalNotes: string }) => {
      setOptimisticNotes({
        internalNotes: notes.internalNotes,
        externalNotes: notes.externalNotes,
      });
    },
    [setOptimisticNotes],
  );

  if (!salesOrder) {
    return null;
  }

  return (
    <EntityTabNotes
      key={salesOrderId}
      className={cn(
        '@SalesOrderTabNotes',
        variant === 'embedded' &&
          'rounded-none rounded-b-lg px-1 pt-4 [&>div]:p-0',
      )}
      counterparty="customer"
      isReadonly={isReadonly}
      internalNotes={effectiveNotes.internalNotes}
      externalNotes={effectiveNotes.externalNotes}
      onSave={handleSave}
      onChange={handleChange}
    />
  );
};
